import { Body, Close, Footer, FooterItem, Header, Modal } from "@zendeskgarden/react-modals";
import { ReactElement, useEffect, useRef, useState } from "react";
import { Property } from "../../../contexts/properties/PropertiesContext";
import * as modalCss from "./shareModal.module.scss";
import { ThemeProvider } from "@zendeskgarden/react-theming";
import { ViziblyTheme } from "../../analyst/ViziblyZDGTheme";
import { Field, Hint, Input, Label, Radio, Textarea } from "@zendeskgarden/react-forms";
import { Button } from "@zendeskgarden/react-buttons";
import { Alert } from "@zendeskgarden/react-notifications";
import { Tooltip } from "@zendeskgarden/react-tooltips";
import {
    CreateSharedReportLinksMutationVariables,
    CreatedSharedReportLinkViewModel,
    PersonEmailInput,
    SharedReportAccessMethod,
    useCreateSharedReportLinksMutation,
    useGetSharedReportLinksLazyQuery,
    useRemoveSharedReportLinkMutation,
    useResendSharedReportLinkMutation,
    useRotateSharedReportLinkPasswordMutation } from "../../../__generated__/generated_types";
import { toast } from "react-toastify";
import { ReactComponent as UserIcon } from '@zendeskgarden/svg-icons/src/16/user-circle-stroke.svg';
import { ReactComponent as TrashIcon } from '@zendeskgarden/svg-icons/src/16/trash-stroke.svg';
import { ReactComponent as LinkIcon } from '@zendeskgarden/svg-icons/src/16/link-stroke.svg';
import { ReactComponent as CheckIcon } from '@zendeskgarden/svg-icons/src/16/check-lg-stroke.svg';
import { ReactComponent as LockIcon } from '@zendeskgarden/svg-icons/src/16/lock-locked-stroke.svg';
import {
    BsThreeDotsVertical as ThreeDotsIcon,
    BsEnvelope  as EnvelopeIcon,
    BsEye as KeyIcon
 } from "react-icons/bs";
import { Dropdown, Item, Menu, Trigger } from "@zendeskgarden/react-dropdowns";


type IShareModalProps = {
    property: Property,
    onClose: () => void
}

type ShareStep = "list" | "message" | "credentials";

type CreatedShareCredentials = Pick<CreatedSharedReportLinkViewModel, "id" | "email" | "accessMethod" | "passwordAccessUrl" | "password">;

function formatShareCredentials(url: string, password: string): string {
    return `Vizibly executive summary\nLink: ${url}\nPassword: ${password}`;
}

function copyShareCredentials(url: string, password: string): void {
    navigator.clipboard.writeText(formatShareCredentials(url, password));
}

type StagedUserData = {
    email: string | null;
    firstName: string | null;
    lastName: string | null;
    accessMethod: SharedReportAccessMethod | null;
}

type StagedUserDataForDisplay = StagedUserData & {
    uniq: number
}

export function ShareModal(props: IShareModalProps): ReactElement {
    const [getSharerReportLinks, {data: sharedReportLinksData, loading: sharedReportLinksLoading}] = useGetSharedReportLinksLazyQuery({fetchPolicy: "no-cache"});
    const [removeSharedReportLink] = useRemoveSharedReportLinkMutation({notifyOnNetworkStatusChange: true});
    const [resendSharedReportLink] = useResendSharedReportLinkMutation({fetchPolicy: "no-cache"});
    const [createSharedReportLinks, {loading: createLinksLoading}] = useCreateSharedReportLinksMutation({notifyOnNetworkStatusChange: true});
    const [rotateSharedReportLinkPassword, {loading: rotatePasswordLoading}] = useRotateSharedReportLinkPasswordMutation({notifyOnNetworkStatusChange: true});

    // const [stagedUsers, setStagedUsers] = useState<StagedUserDataForDisplay[]>([{uniq: Date.now(), email: null, firstName: null, lastName: null}]);
    const [stagedUsers, setStagedUsers] = useState<StagedUserDataForDisplay[]>([]);
    const [inviteMessage, setInviteMessage] = useState<string>();
    const [stagedUsersValid, setStagedUsersValid] = useState(false);
    const [shareStep, setShareStep] = useState<ShareStep>("list");
    const [createdCredentials, setCreatedCredentials] = useState<CreatedShareCredentials[]>([]);
    const [copiedCredentialId, setCopiedCredentialId] = useState<string | null>(null);
    const [pendingPasswordRotate, setPendingPasswordRotate] = useState<{id: string, email: string} | null>(null);
    const stagedUsersDivRef = useRef<HTMLDivElement>(null);
    function handleAddBlankStagedUser() {
        setStagedUsers(prev => prev.concat({uniq: Date.now(), email: null, firstName: null, lastName: null, accessMethod: SharedReportAccessMethod.Otp}));
        setTimeout(() => {
            const div = stagedUsersDivRef.current;
            if (div) {
                div.scrollTop = div.scrollHeight;
            }
        }, 500);
    }

    function validateStagedUsers(stagedUsers: StagedUserDataForDisplay[]) {
        let stagedUsersValid = stagedUsers.length > 0;
        for (const stagedUser of stagedUsers) {
            stagedUsersValid = stagedUsersValid
            && stagedUser.email !== null
            && stagedUser.firstName !== null
            && stagedUser.lastName !== null
            && stagedUser.accessMethod !== null;
            if (!stagedUsersValid) {
                break;
            }
        }
        setStagedUsersValid(stagedUsersValid);
    }

    function handleRemoveStagedUser(index: number) {
        if (index >= 0) {
            setStagedUsers(prev => {
                let updated = prev;
                if (prev.length > index) {
                    updated = [...prev];
                    updated.splice(index, 1);
                }
                return updated;
            });
        }
    }

    function handleUpdateStagedUser(
            index: number,
            email: string | null,
            firstName: string | null,
            lastName: string | null,
            accessMethod: SharedReportAccessMethod | null) {
        if (index >= 0 && index < stagedUsers.length) {
            const updated = stagedUsers.map((user, i) => {
                let updatedUser = user;
                if (i === index) {
                    updatedUser = {...user};
                    updatedUser.email = email;
                    updatedUser.firstName = firstName;
                    updatedUser.lastName = lastName;
                    updatedUser.accessMethod = accessMethod;
                }
                return updatedUser;
            });
            setStagedUsers(updated);
        }
    }

    function handleInvite() {
        if (stagedUsers.length == 0) {
            return;
        }
        const persons:PersonEmailInput[] = []
        const vars:CreateSharedReportLinksMutationVariables = {
            budgetYear: props.property.budgetYear,
            propertyId: props.property.id,
            persons: persons
        };
        for (const user of stagedUsers) {
            if (user.email === null || user.firstName === null || user.lastName == null || user.accessMethod === null) {
                return;
            }
            persons.push({
                email: user.email,
                firstName: user.firstName,
                lastName: user.lastName,
                accessMethod: user.accessMethod
            });
        }
        if (inviteMessage && inviteMessage.trim().length != 0) {
            vars.message = inviteMessage;
        }
        createSharedReportLinks({variables: vars})
        .then((data) => {
            const created = data.data?.createSharedReportLinksV2;
            if (created && created.length > 0) {
                setCreatedCredentials(created);
                setCopiedCredentialId(null);
                setStagedUsers([]);
                getSharerReportLinks({
                    variables: {
                        budgetYear: props.property.budgetYear,
                        propertyId: props.property.id
                    }
                });
                const otpCreated = created.filter(c => c.accessMethod === SharedReportAccessMethod.Otp);
                const passwordCreated = created.filter(c => c.accessMethod === SharedReportAccessMethod.Password);
                if (otpCreated.length > 0) {
                    toast.success(`Access email sent to ${otpCreated.map(c => c.email).join(", ")}`);
                }
                if (passwordCreated.length > 0) {
                    setShareStep("credentials");
                }
                else {
                    setShareStep("list");
                }
            }
        });
    }

    function handleSharedLinkAction(id: string, email: string, link: string, action: SharedLinkAction) {
        if (action === "copy") {
            navigator.clipboard.writeText(link);
            toast.success(`Access link copied to clipboard`);
        }
        else if (action === "remove") {
            removeSharedReportLink({
                variables: {
                    id: id
                }
            })
            .then((data) => {
                const ret = data.data;
                if (ret) {
                    toast.success(`Access removed for ${email}`);
                }
                getSharerReportLinks({
                    variables: {
                        budgetYear: props.property.budgetYear,
                        propertyId: props.property.id
                    }
                });
            });
        }
        else if (action === "copyPassword") {
            if (rotatePasswordLoading) {
                return;
            }
            setPendingPasswordRotate({id, email});
        }
        else if (action === "resend") {
            resendSharedReportLink({
                variables: {
                    id: id
                }
            })
            .then((data) => {
                const ret = data.data;
                if (ret) {
                    toast.success(`Access email sent to ${email}`);
                }
            });
        }
    }

    function copyCredentials(cred: CreatedShareCredentials) {
        if (!cred.passwordAccessUrl || !cred.password) {
            return;
        }
        copyShareCredentials(cred.passwordAccessUrl, cred.password);
        setCopiedCredentialId(cred.id);
    }

    function handleCredentialsDone() {
        setCreatedCredentials([]);
        setCopiedCredentialId(null);
        setShareStep("list");
        getSharerReportLinks({
            variables: {
                budgetYear: props.property.budgetYear,
                propertyId: props.property.id
            }
        });
    }

    function confirmRotatePassword() {
        if (!pendingPasswordRotate || rotatePasswordLoading) {
            return;
        }
        rotateSharedReportLinkPassword({
            variables: {
                id: pendingPasswordRotate.id
            }
        })
        .then((data) => {
            const rotated = data.data?.rotateSharedReportLinkPassword;
            setPendingPasswordRotate(null);
            if (rotated && rotated.password && rotated.passwordAccessUrl) {
                copyShareCredentials(rotated.passwordAccessUrl, rotated.password);
                setCreatedCredentials([rotated]);
                setCopiedCredentialId(rotated.id);
                setShareStep("credentials");
            }
        });
    }

    useEffect(() => {
        if (!copiedCredentialId) {
            return;
        }
        const timer = setTimeout(() => {
            setCopiedCredentialId(null);
        }, 2500);
        return () => {
            clearTimeout(timer);
        };
    }, [copiedCredentialId]);

    useEffect(() => {
        validateStagedUsers(stagedUsers);
    }, [stagedUsers]);

    useEffect(() => {
        getSharerReportLinks({
            variables: {
                budgetYear: props.property.budgetYear,
                propertyId: props.property.id
            }
        });
    },[]);

    const otpGuests = stagedUsers.filter(u => u.accessMethod === SharedReportAccessMethod.Otp);
    const passwordGuests = stagedUsers.filter(u => u.accessMethod === SharedReportAccessMethod.Password);

    return (
        <ThemeProvider theme={ViziblyTheme}>
            <>
            <Modal onClose={() => props.onClose()} isLarge style={{ overflow: "unset" }}>
                <Close aria-label="Close modal" />
                <Header>
                    Share Executive Summary ({props.property.name})
                </Header>
                <Body className={modalCss.modalBody}>
                    {shareStep === "list" &&
                    <>
                        <span className={modalCss.description}>Choose how each guest gets in. Vizibly can email them a sign-in code, or you can send a link and password yourself. To switch later, remove the guest and add them again.</span>
                        <div className={modalCss.stagedUsersContainer} ref={stagedUsersDivRef}>
                            {stagedUsers.map((su, index) =>
                                <StagedUser
                                    key={su.uniq}
                                    data={su}
                                    handleRemove={() => handleRemoveStagedUser(index)}
                                    handleChange={(...args) => handleUpdateStagedUser(index, ...args)}/>
                            )}
                        </div>
                        <Button
                            className={modalCss.addGuestButton}
                            isBasic
                            onClick={() => handleAddBlankStagedUser()}
                        >
                            + Add guest
                        </Button>
                        <span>GUEST ACCESS</span>
                        <div className={modalCss.sharedLinksContainer}>
                            {sharedReportLinksData?.getSharedReportLinks.map(sharedReportLink =>
                                <SharedLink
                                    key={sharedReportLink.id}
                                    email={sharedReportLink.email}
                                    firstName={sharedReportLink.firstName}
                                    lastName={sharedReportLink.lastName}
                                    accessMethod={sharedReportLink.accessMethod}
                                    handleAction={(action) => handleSharedLinkAction(sharedReportLink.id, sharedReportLink.email, sharedReportLink.url, action)}
                                />
                            )}
                        </div>
                    </>
                    }
                    {shareStep === "message" &&
                    <>
                        {otpGuests.length > 0 &&
                        <div className={modalCss.inviteReviewGroup}>
                            <span>EMAIL CODE</span>
                            <InviteReviewRows users={otpGuests}/>
                            <Field>
                                <Label>Optional message</Label>
                                <Textarea placeholder="Included in the invite email" value={inviteMessage} onChange={(evt) => setInviteMessage(evt.target.value)}/>
                            </Field>
                        </div>
                        }
                        {otpGuests.length > 0 && passwordGuests.length > 0 &&
                        <hr className={modalCss.inviteReviewDivider}/>
                        }
                        {passwordGuests.length > 0 &&
                        <div className={modalCss.inviteReviewGroup}>
                            <span>SHARE PASSWORD</span>
                            <InviteReviewRows users={passwordGuests}/>
                            <Alert type="info">
                            Please copy a link and password for each guest. Vizibly will not email the user.
                            </Alert>
                        </div>
                        }
                    </>
                    }
                    {shareStep === "credentials" &&
                    <>
                        {createdCredentials.some(c => c.accessMethod === SharedReportAccessMethod.Otp) &&
                        <span className={modalCss.description}>
                            We emailed a sign-in invite to {createdCredentials.filter(c => c.accessMethod === SharedReportAccessMethod.Otp).map(c => c.email).join(", ")}.
                        </span>
                        }
                        <span className={modalCss.credentialsWarning}>This password is shown once. Copy it now. If you need it again later, generate a new one from the guest list — the old password will stop working.</span>
                        <div className={modalCss.credentialsList}>
                            {createdCredentials.filter(cred => cred.accessMethod === SharedReportAccessMethod.Password && cred.password && cred.passwordAccessUrl).map(cred => {
                                const copied = copiedCredentialId === cred.id;
                                return (
                                <div key={cred.id} className={modalCss.credentialCard}>
                                    <div className={modalCss.credentialEmail}>{cred.email}</div>
                                    <div className={modalCss.credentialSecret}>Link: {cred.passwordAccessUrl}</div>
                                    <div className={modalCss.credentialSecret}>Password: {cred.password}</div>
                                    <Button
                                        className={modalCss.copyButton}
                                        isPrimary
                                        onClick={() => copyCredentials(cred)}
                                    >
                                        {copied ? <><CheckIcon/> Copied</> : "Copy link and password"}
                                    </Button>
                                    {copied &&
                                    <span className={modalCss.copiedHint}>
                                        Copied. Paste this into an email or anywhere else you share with your guest.
                                    </span>
                                    }
                                </div>
                                );
                            })}
                        </div>
                    </>
                    }
                </Body>
                <Footer className={modalCss.modalFooter}>
                    <FooterItem>
                        {shareStep === "credentials" ?
                        <Button isPrimary onClick={() => handleCredentialsDone()}>
                            Done
                        </Button>
                        :
                        <Button isBasic isDanger onClick={() => props.onClose()}>
                            Cancel
                        </Button>
                        }
                    </FooterItem>
                    <FooterItem>
                        {shareStep === "list" &&
                        <Button isPrimary disabled={!stagedUsersValid} onClick={() => setShareStep("message")}>
                            Next
                        </Button>
                        }
                        {shareStep === "message" &&
                        <div className={modalCss.modalFooterRightButtonContainer}>
                            <Button isBasic onClick={() => setShareStep("list")}>
                                Back
                            </Button>
                            <Button isPrimary onClick={() => handleInvite()} disabled={createLinksLoading}>
                                Invite
                            </Button>
                        </div>
                        }
                    </FooterItem>
                </Footer>
            </Modal>
            {pendingPasswordRotate &&
            <Modal onClose={() => setPendingPasswordRotate(null)}>
                <Header isDanger>Create a new password?</Header>
                <Body>
                    This creates a new password and the previous password will stop working.
                </Body>
                <Footer>
                    <FooterItem>
                        <Button isBasic onClick={() => setPendingPasswordRotate(null)}>
                            Cancel
                        </Button>
                    </FooterItem>
                    <FooterItem>
                        <Button isPrimary isDanger onClick={() => confirmRotatePassword()} disabled={rotatePasswordLoading}>
                            Continue
                        </Button>
                    </FooterItem>
                </Footer>
                <Close aria-label="Close modal" />
            </Modal>
            }
            </>
        </ThemeProvider>
    );
}

function checkValid(checkValue:string|null, ...otherValues:(string|null)[]): "success"|"error" {
    let ret:"success"|"error" = "success";
    if (!(checkValue && checkValue.trim().length > 0)) {
        for (const otherValue of otherValues) {
            if (otherValue) {
                ret = "error";
                break;
            }
        }
    }
    return ret;
}

function StagedUser(
    props: {
        data: StagedUserDataForDisplay,
        handleChange: (email: string | null, firstName: string | null, lastName: string | null, accessMethod: SharedReportAccessMethod | null) => void,
        handleRemove: undefined | (() => void)}): ReactElement {
    const [email, setEmail] = useState<string | null>(props.data.email);
    const [firstName, setFirstName] = useState<string | null>(props.data.firstName);
    const [lastName, setLastName] = useState<string | null>(props.data.lastName);
    const [accessMethod, setAccessMethod] = useState<SharedReportAccessMethod | null>(props.data.accessMethod);

    useEffect(() => {
        props.handleChange(
            (!email || email && email.trim(),length == 0 ? null : email),
            (!firstName || firstName && firstName.trim().length == 0 ? null : firstName),
            (!lastName || lastName && lastName.trim().length == 0 ? null : lastName),
            accessMethod
        );
    }, [email, firstName, lastName, accessMethod]);
    return (
        <div className={modalCss.stagedUser}>
            <div className={modalCss.stagedUserHeader}>
                <span>GUEST USER</span>
                <Button isBasic isDanger disabled={!props.handleRemove} onClick={props.handleRemove}>Remove</Button>
            </div>
            <Field>
                <Input
                    placeholder="Invite by email*"
                    value={email ?? undefined}
                    onChange={(evt) => setEmail(evt.target.value)}
                    validation={checkValid(email, firstName, lastName)}
                />
            </Field>
            <div className={modalCss.nameInputs}>
                <Field>
                    <Input
                        placeholder="First Name*"
                        value={firstName ?? undefined}
                        onChange={(evt) => setFirstName(evt.target.value)}
                        validation={checkValid(firstName, email, lastName)}
                    />
                </Field>
                <Field>
                    <Input
                        placeholder="Last Name*"
                        value={lastName ?? undefined}
                        onChange={(evt) => setLastName(evt.target.value)}
                        validation={checkValid(lastName, firstName, email)}
                    />
                </Field>
            </div>
            <div className={modalCss.accessMethodChoice}>
                <Field>
                    <Radio
                        name={`guest-access-${props.data.uniq}`}
                        checked={accessMethod === SharedReportAccessMethod.Otp}
                        onChange={() => setAccessMethod(SharedReportAccessMethod.Otp)}
                    >
                        <Label>Email code</Label>
                        <Hint>We'll email them a sign-in link and a code to open the report.</Hint>
                    </Radio>
                </Field>
                <Field>
                    <Radio
                        name={`guest-access-${props.data.uniq}`}
                        checked={accessMethod === SharedReportAccessMethod.Password}
                        onChange={() => setAccessMethod(SharedReportAccessMethod.Password)}
                    >
                        <Label>Share password</Label>
                        <Hint>You'll copy a link and password and send them yourself. Vizibly will not email this guest.</Hint>
                    </Radio>
                </Field>
            </div>
        </div>
    )
}

function SharedLink(props: {
        email: string,
        firstName: string,
        lastName: string,
        accessMethod: SharedReportAccessMethod,
        handleAction: (action: SharedLinkAction) => void
}): ReactElement {
    const isPassword = props.accessMethod === SharedReportAccessMethod.Password;
    function handleAction(action: SharedLinkAction) {
        props.handleAction(action);
    }
    return (
        <div className={modalCss.sharedLink}>
            <UserIcon/>
            <div className={modalCss.person}>
                {(props.firstName.length > 0 || props.lastName.length > 0) && <div className={modalCss.personName}>{props.firstName} {props.lastName}</div>}
                <div className={modalCss.personEmail}>{props.email}</div>
            </div>
            <div className={modalCss.sharedLinkEnd}>
            <AccessMethodBadge accessMethod={props.accessMethod} alreadyShared/>
            <div className={modalCss.menuButton}>
                <Dropdown
                    onSelect={action => handleAction(action)}
                >
                    <Trigger>
                        <Button isBasic className={modalCss.sharedLinkMenuButton}>
                            <ThreeDotsIcon/>
                        </Button>
                    </Trigger>

                    <Menu>
                        {!isPassword && <Item value="resend"><EnvelopeIcon/>&nbsp;Resend email</Item>}
                        {!isPassword && <Item value="copy"><LinkIcon/>&nbsp;Copy access link</Item>}
                        {isPassword && <Item value="copyPassword"><KeyIcon/>&nbsp;Copy link and password</Item>}
                        <Item value="remove"><TrashIcon/>&nbsp;Remove access</Item>
                    </Menu>
                </Dropdown>
            </div>
            </div>
        </div>
    );
}

type SharedLinkAction = "resend"|"copy"|"copyPassword"|"remove";

function InviteReviewRows(props: {users: StagedUserDataForDisplay[]}): ReactElement {
    return (
        <div className={modalCss.inviteReviewList}>
            {props.users.map(u =>
                <div className={modalCss.inviteReviewRow} key={u.uniq}>
                    <div className={modalCss.person}>
                        {(u.firstName || u.lastName) && <div className={modalCss.personName}>{u.firstName} {u.lastName}</div>}
                        <div className={modalCss.personEmail}>{u.email}</div>
                    </div>
                </div>
            )}
        </div>
    );
}

function AccessMethodBadge(props: {accessMethod: SharedReportAccessMethod, alreadyShared?: boolean}): ReactElement {
    const isPassword = props.accessMethod === SharedReportAccessMethod.Password;
    const alreadyShared = props.alreadyShared === true;
    return (
        <Tooltip
            content={isPassword
                ? (alreadyShared
                    ? "This guest signs in with a link and password you send. Vizibly does not email them."
                    : "You will send this guest a link and password. Vizibly will not email them.")
                : (alreadyShared
                    ? "Vizibly emailed this guest a sign-in code."
                    : "Vizibly will email this guest a sign-in code.")}
            placement="top"
        >
            <span className={modalCss.accessMethodBadge}>
                {isPassword ? <LockIcon/> : <EnvelopeIcon/>}
                {isPassword
                    ? (alreadyShared ? "Shared password" : "Share password")
                    : (alreadyShared ? "Emailed code" : "Email code")}
            </span>
        </Tooltip>
    );
}